import React, { useEffect, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import Table from "react-bootstrap/Table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import HelpButton from "../HelpContent/HelpButton";
import MyIcon from "../Common/MyIcon";
import UserIcon from "../../assets/icons/user_group-01.svg2";
import MySearch from "../Common/MySearch";
import MyPagination from "../Common/MyPagination";
import {
  useDeleteUserMutation,
  useGetUsersSettingsDataQuery,
  useGetUsersSettingsFilterDataQuery,
  useUpdateUserLockedMutation,
} from "../store/slices/settingsSlice";
import AddUsersModal from "./AddUsersModal";
import Input from "../Builder/nodes/Logic/Inputs/Input";
import { toast } from "react-toastify";
import { isUserPermitted } from "../helpers/pageProps";
import { useConfirmDialog } from "../Builder/Components/ConfirmDialogContext";

export default function ManageUsers({ collapsed, setCollapsed }) {
  const confirmDialog = useConfirmDialog();
  const [params, setParams] = useState({ limit: 12, offset: 0, order: "asc" });
  const { data: { rows: users, total } = { rows: [], total: 0 } } =
    useGetUsersSettingsDataQuery(params);
  const { data: products } = useGetUsersSettingsFilterDataQuery();
  const [search, setSearch] = useState();
  useEffect(() => {
    const t = setTimeout(() => {
      setParams((p) => ({ ...p, search }));
    }, 500);
    return () => clearTimeout(t);
  }, [search]);

  const [updateLocked] = useUpdateUserLockedMutation();
  const [deleteUser] = useDeleteUserMutation();

  const handleDelete = async (userId) => {
    if (
      !(await confirmDialog(
        "Delete User",
        "Are you sure you want to delete this user?",
        true,
        [
          {
            text: "Yes, and delete associated contacts",
            onClick: async () => {
              const r = await deleteUser({
                userId,
                body: { deleteAsscContact: true },
              });
              if (r.data?.status === "success") {
                toast.success("User deleted successfully");
                return true;
              }
              toast.error(r.data?.message || "An error occurred");
              return false;
            },
          },
        ]
      ))
    ) {
      return;
    }
    const r = await deleteUser({ userId, body: { deleteAsscContact: false } });
    if (r.data?.status === "success") {
      toast.success("User deleted successfully");
      return;
    }
    toast.error(r.data?.message || "An error occurred");
  };

  return (
    <div>
      <Row>
        <Col>
          <div className="bg-purple-gradient-darker-reverse rounded-4 px-3 mb-2 py-2">
            <div className="d-flex justify-content-between">
              <div>
                <h5 className=" d-flex align-items-center">
                  <MyIcon Icon={UserIcon} className="me-2" />
                  <span className="ms-2">
                    <span className="fw-bold me-2"> Manage Users:</span>
                    <HelpButton helpKey="settings-users-manage-users" />
                    <div className="small text-secondary">
                      Manage user details
                    </div>
                  </span>
                </h5>
              </div>
              <div className="d-flex align-items-center gap-2">
                <div>
                  <Form.Select
                    aria-label="newTemplate-select"
                    onChange={(e) => {
                      setParams((p) => {
                        if (e.target.value === "") {
                          const { "products[]": pp, ...rest } = p;
                          return rest;
                        }
                        return {
                          ...p,
                          "products[]": e.target.value,
                        };
                      });
                    }}
                    value={params["products[]"]}
                  >
                    <option value="">Show all</option>
                    {products?.map((p) => (
                      <option value={p.id}>
                        {p.product_name}, {p.product_type}
                      </option>
                    ))}
                  </Form.Select>
                </div>
                <div>
                  <MySearch
                    placeholder="Search"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                    }}
                  />
                </div>
                {isUserPermitted("edit_user") && (
                  <div
                    className="d-flex align-items-center px-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <AddUsersModal />
                  </div>
                )}
              </div>
            </div>
            <div className="bg-white p-4 rounded-3 mb-4 my-2">
              <div
                className="table-responsive"
                style={{
                  maxHeight: collapsed ? "200px" : "none",
                  overflowY: "auto",
                }}
              >
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>User Name</th>
                      <th>Products</th>
                      <th>Role</th>
                      <th>Email</th>
                      <th>Mobile</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {users?.map((m) => (
                      <tr key={m.data.id}>
                        <td>
                          <span>
                            {m.data.first_name} {m.data.last_name}
                          </span>
                        </td>
                        <td>
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                            }}
                          >
                            {m.products_raw?.map((p) => (
                              <div className="text-bg-secondary p-1 rounded-1 text-nowrap small me-1 mb-1">
                                {p.product_name} ({p.product_type})
                              </div>
                            ))}
                          </div>
                        </td>
                        <td>
                          <span>
                            {m.data.role_id === 1 ? (
                              "Super Admin"
                            ) : m.data.role_id === 2 ? (
                              "Controller"
                            ) : (
                              <div
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                }}
                              >
                                {m.assigned_user_roles.map((r) => (
                                  <div>
                                    <div className="text-bg-secondary p-1 rounded-1 small me-1 mb-1">
                                      {r.name}
                                      {r.accounts.map((a) => (
                                        <span className="badge bg-light ms-1 text-dark text-nowrap">
                                          {a.product_name} ({a.product_type})
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </span>
                        </td>
                        <td>{m.data.email}</td>
                        <td>{m.data.mobile}</td>
                        <td>
                          {isUserPermitted("edit_user") ? (
                            <div className="d-flex align-items-center justify-content-between">
                              <span className="me-2">
                                <AddUsersModal userId={m.data.id} />
                              </span>
                              <span className="me-2">
                                <Input
                                  inputType="radio"
                                  radioName={`${m.data.id}-lock-radio`}
                                  value={[m.data.locked]}
                                  onChange={([v]) => {
                                    updateLocked({
                                      userId: m.data.id,
                                      locked: v === "Y",
                                    });
                                  }}
                                  options={[
                                    { value: "Y", label: "Locked" },
                                    { value: "N", label: "Unlocked" },
                                  ]}
                                />
                              </span>
                              <button
                                type="button"
                                className="btn btn-secondary rounded-pill fw-bold btn-sm"
                                onClick={() => handleDelete(m.data.id)}
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </div>
                          ) : (
                            <span
                              className="text-secondary"
                              title="You do not have enough permissions to access this feature"
                            >
                              No Access
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              <button
                type="button"
                onClick={() => setCollapsed(!collapsed)}
                className="btn btn-link p-0 mt-2"
              >
                {collapsed ? "Show More" : "Show Less"}
              </button>
              <MyPagination
                total={total}
                onPaginate={(page, perPage) => {
                  setParams((p) => ({
                    ...p,
                    limit: perPage,
                    offset: page * perPage,
                  }));
                }}
              />
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
}
